import React, {useEffect, useState} from 'react';
import axios from 'axios';
import {enqueueSnackbar} from 'notistack';
import {KeenIcon} from '@/components';
import {TimelinesWrapper} from '@/partials/timelines/default/item';
import {formatDateOnly} from '@/pages/buddy/utils/Date';
import {LoaderType, Loading} from "@/layouts/buddy/loading";

interface JobItem {
    is_gap: boolean;
    gap_reason?: string | null;
    title: string;
    company: string;
    start_date: string | null;
    end_date: string | null;
    responsibilities: string[];
    country: string | null;
    city: string | null;
    location_type: string | null;
    job_type: string | null;
}

interface JobHistoryProps {
    title: string;
    items: JobItem[];
    candidate_p_id: string;
    loadingJobHistory: boolean;
}

/** Combine city + country -> location */
function transformForEditing(items: any): (JobItem & { location?: string })[] {
    if (!Array.isArray(items)) {
        console.warn('transformForEditing received a non-array value:', items);
        return [];
    }
    return items.map((item) => ({
        ...item,
        location: [item.city, item.country].filter(Boolean).join(', '),
    }));
}

/** Re-split location -> city, country */
function transformForSaving(editedItems: (JobItem & { location?: string })[]) {
    return editedItems.map((item) => {
        const [cityStr, countryStr] = (item.location || '').split(',').map((s) => s.trim());
        return {
            ...item,
            city: cityStr || '',
            country: countryStr || '',
        };
    });
}

function deepCloneJobItem(item: JobItem & { location?: string }) {
    return {
        ...item,
        responsibilities: [...item.responsibilities],
    };
}

function deepCloneItems(items: (JobItem & { location?: string })[]) {
    return items.map(deepCloneJobItem);
}

function daysBetweenDates(date1: string, date2: string): number {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    const msPerDay = 1000 * 60 * 60 * 24;
    return Math.round((d2.getTime() - d1.getTime()) / msPerDay);
}

function buildGapDurationString(numDays: number): string {
    const approxYears = Math.floor(numDays / 365);
    const remainderAfterYears = numDays % 365;
    const approxMonths = Math.floor(remainderAfterYears / 30);
    const remainderAfterMonths = remainderAfterYears % 30;
    const days = remainderAfterMonths;

    const parts: string[] = [];
    if (approxYears > 0) parts.push(`${approxYears} year${approxYears > 1 ? 's' : ''}`);
    if (approxMonths > 0) parts.push(`${approxMonths} month${approxMonths > 1 ? 's' : ''}`);
    if (days > 0) parts.push(`${days} day${days > 1 ? 's' : ''}`);

    if (parts.length === 0) {
        return 'Less than a month';
    }
    return parts.join(' ');
}

interface GapRecord {
    __isGap: true;
    gapText: string;
}

/**
 * insertGapItems:
 * Compare each job's end_date to the next job's start_date (assuming descending order).
 * If the gap is greater than 30 days, insert a gap record.
 */
function insertGapItems(items: (JobItem & { location?: string })[]): Array<(JobItem & { location?: string }) | GapRecord> {
    if (items.length < 2) return items;

    const merged: Array<(JobItem & { location?: string }) | GapRecord> = [];

    for (let i = 0; i < items.length; i++) {
        merged.push(items[i]);
        if (i < items.length - 1) {
            const currentJob = items[i];
            const nextJob = items[i + 1];
            if (currentJob.end_date && nextJob.start_date) {
                const gapInDays = daysBetweenDates(currentJob.end_date, nextJob.start_date);
                if (gapInDays > 30) {
                    merged.push({
                        __isGap: true,
                        gapText: `Gap in between jobs of ${buildGapDurationString(gapInDays)}`,
                    });
                }
            }
        }
    }
    return merged;
}

function sortJobsDescending(items: (JobItem & { location?: string })[]): (JobItem & { location?: string })[] {
    return [...items].sort((a, b) => {
        const dateA = a.start_date ? new Date(a.start_date).getTime() : 0;
        const dateB = b.start_date ? new Date(b.start_date).getTime() : 0;
        return dateB - dateA;
    });
}

const JobHistory = ({ title, items, candidate_p_id, loadingJobHistory }: JobHistoryProps) => {
    const API_URL = import.meta.env.VITE_APP_API_URL;

    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [originalItems, setOriginalItems] = useState<(JobItem & { location?: string })[]>([]);
    const [editedItems, setEditedItems] = useState<(JobItem & { location?: string })[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        const transformed = transformForEditing(items);
        setOriginalItems(deepCloneItems(transformed));
        setEditedItems(deepCloneItems(transformed));
    }, [items]);

    const calculateDuration = (startDate: string, endDate: string | null) => {
        const start = new Date(startDate);
        const today = endDate ? new Date(endDate) : new Date();

        const years = today.getFullYear() - start.getFullYear();
        const months = today.getMonth() - start.getMonth();
        const days = today.getDate() - start.getDate();

        let duration = '';
        if (years > 0) duration += `${years} year${years > 1 ? 's' : ''}`;
        if (months > 0) {
            if (duration) duration += ' ';
            duration += `${months} month${months > 1 ? 's' : ''}`;
        }
        if (days > 0) {
            if (duration) duration += ' ';
            duration += `${days} day${days > 1 ? 's' : ''}`;
        }
        if (!duration) duration = 'Less than a month';
        return duration;
    };

    const handleEditClick = () => {
        setEditedItems(deepCloneItems(originalItems));
        setIsEditing(true);
    };
    const handleCancelClick = () => {
        setEditedItems(deepCloneItems(originalItems));
        setIsEditing(false);
    };

    const handleSaveClick = async () => {
        setLoading(true);
        // Validation for non-gap jobs and gap items
        for (const item of editedItems) {
            if (!item.is_gap) {
                if (!item.title || !item.title.trim()) {
                    enqueueSnackbar("Every job must have a title.", { variant: 'warning' });
                    setLoading(false);
                    return;
                }
                if (item.start_date && item.end_date) {
                    if (new Date(item.start_date) > new Date(item.end_date)) {
                        enqueueSnackbar("The start date of a job cannot be after end date.", { variant: 'warning' });
                        setLoading(false);
                        return;
                    }
                }
            } else {
                if (!item.start_date || !item.start_date.trim()) {
                    enqueueSnackbar("Every gap must have a start date.", { variant: 'warning' });
                    setLoading(false);
                    return;
                }
                if (!item.end_date || !item.end_date.trim()) {
                    enqueueSnackbar("Every gap must have an end date.", { variant: 'warning' });
                    setLoading(false);
                    return;
                }
                if (!item.gap_reason || !item.gap_reason.trim()) {
                    enqueueSnackbar("Every gap must have a reason.", { variant: 'warning' });
                    setLoading(false);
                    return;
                }
            }
        }

        try {
            const token = localStorage.getItem('authToken') || '';
            const dataToSend = transformForSaving(editedItems);
            await axios.put(
                `${API_URL}/candidate/${candidate_p_id}/job-history`,
                { job_history: dataToSend },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );
            const transformedNewOriginal = transformForEditing(dataToSend);
            setOriginalItems(deepCloneItems(transformedNewOriginal));
            setEditedItems(deepCloneItems(transformedNewOriginal));
            setIsEditing(false);
        } catch (err) {
            enqueueSnackbar(`Failed to update job history: ${err}`, {
                variant: 'solid',
                state: 'danger',
            });
        } finally {
            setLoading(false);
        }
    };

    // Functions for adding new jobs or gaps
    const createNewJob = (): JobItem & { location?: string } => ({
        title: '',
        company: '',
        start_date: '',
        end_date: '',
        responsibilities: [],
        city: '',
        country: '',
        location_type: '',
        job_type: '',
        location: '',
        is_gap: false,
        gap_reason: null
    });

    const createNewGap = (): JobItem & { location?: string } => ({
        title: '',
        company: '',
        start_date: '',
        end_date: '',
        responsibilities: [],
        city: '',
        country: '',
        location_type: '',
        job_type: '',
        location: '',
        is_gap: true,
        gap_reason: ''
    });

    const handleAddJob = (atTop: boolean = false) => {
        setEditedItems((prev) => {
            const newItems = deepCloneItems(prev);
            if (atTop) {
                newItems.unshift(createNewJob());
            } else {
                newItems.push(createNewJob());
            }
            return newItems;
        });
    };

    const handleAddGap = (atTop: boolean = false) => {
        setEditedItems((prev) => {
            const newItems = deepCloneItems(prev);
            if (atTop) {
                newItems.unshift(createNewGap());
            } else {
                newItems.push(createNewGap());
            }
            return newItems;
        });
    };

    const handleDeleteJob = (index: number) => {
        setEditedItems((prev) => {
            const newItems = deepCloneItems(prev);
            newItems.splice(index, 1);
            return newItems;
        });
    };

    type EditableStringFields =
        | 'title'
        | 'company'
        | 'start_date'
        | 'end_date'
        | 'gap_reason'
        | 'country'
        | 'city'
        | 'location_type'
        | 'job_type'
        | 'location';

    const handleEditField = (index: number, field: EditableStringFields) =>
        (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
            setEditedItems((prev) => {
                const newItems = deepCloneItems(prev);
                newItems[index][field] = e.target.value;
                return newItems;
            });
        };

    const handleAddResponsibility = (jobIndex: number) => {
        setEditedItems((prev) => {
            const newItems = deepCloneItems(prev);
            newItems[jobIndex].responsibilities.push('');
            return newItems;
        });
    };

    const handleEditResponsibility =
        (jobIndex: number, respIndex: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
            setEditedItems((prev) => {
                const newItems = deepCloneItems(prev);
                newItems[jobIndex].responsibilities[respIndex] = e.target.value;
                return newItems;
            });
        };

    const handleDeleteResponsibility = (jobIndex: number, respIndex: number) => {
        setEditedItems((prev) => {
            const newItems = deepCloneItems(prev);
            newItems[jobIndex].responsibilities.splice(respIndex, 1);
            return newItems;
        });
    };

    if (loadingJobHistory)
        return (
            <div className="card">
                <div className="card-header flex justify-between items-center">
                    <h3 className="card-title">{title}</h3>
                </div>
                <div className="card-body flex justify-center items-center">
                    <Loading loadingText={"Processing Job History"} loaderType={LoaderType.ROBOT_WAITING} loading={loadingJobHistory} />
                </div>
            </div>
        );

    return (
        <div className="card">
            <div className="card-header flex justify-between items-center">
                <h3 className="card-title">{title}</h3>
                {!isEditing ? (
                    <button className="btn btn-light btn-sm" onClick={handleEditClick}>
                        <KeenIcon icon="notepad-edit" />
                        Edit
                    </button>
                ) : (
                    <div className="flex space-x-2">
                        <button className="btn btn-primary btn-sm" onClick={handleSaveClick} disabled={loading}>
                            <KeenIcon icon="file-up" />
                            {loading ? 'Saving...' : 'Save'}
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={handleCancelClick} disabled={loading}>
                            <KeenIcon icon="tablet-delete" />
                            Cancel
                        </button>
                    </div>
                )}
            </div>

            <div className="card-body">
                {!isEditing ? (
                    // NON-EDIT MODE: Render sorted jobs with inserted gap records and proper gap styling.
                    (() => {
                        const sortedDescending = sortJobsDescending(editedItems);
                        const merged = insertGapItems(sortedDescending);
                        return merged.map((item, i) => {
                            // Render inserted gap records
                            if ((item as any).__isGap) {
                                return (
                                    <TimelinesWrapper key={`gap-${i}`} icon="alarm" line>
                                        <div className="text-sm flex items-center justify-between">
                                            <span className="font-medium text-red-600">
                                                {(item as any).gapText}
                                            </span>
                                        </div>
                                    </TimelinesWrapper>
                                );
                            }
                            const job = item as JobItem & { location?: string };

                            // Render actual gap job entries differently
                            if (job.is_gap) {
                                const startDate = job.start_date ? formatDateOnly(job.start_date) : '-';
                                let endDate = job.end_date ? formatDateOnly(job.end_date) : 'Until today';
                                let duration = '';
                                if (job.start_date && job.end_date) {
                                    duration = `(${calculateDuration(job.start_date, job.end_date)})`;
                                } else if (job.start_date && !job.end_date) {
                                    duration = `(${calculateDuration(job.start_date, null)})`;
                                    endDate = 'Until today';
                                }
                                return (
                                    <TimelinesWrapper key={`gapdata-${i}`} icon="abstract-22" line>
                                        <div className="text-sm font-semibold text-red-500">
                                            Gap in timeline
                                        </div>
                                        <div className="flex items-center gap-2 mt-2 text-sm text-gray-700">
                                            <span>{startDate} - {endDate}</span>
                                            <span className="text-gray-500">{duration}</span>
                                        </div>
                                        {job.gap_reason && (
                                            <div className="mt-1 text-sm text-gray-700">
                                                Reason: {job.gap_reason}
                                            </div>
                                        )}
                                    </TimelinesWrapper>
                                );
                            }

                            // Render normal job entries
                            const startDate = job.start_date ? formatDateOnly(job.start_date) : '-';
                            let endDate = job.end_date ? formatDateOnly(job.end_date) : 'Until today';
                            let duration = '';
                            if (job.start_date && job.end_date) {
                                duration = `(${calculateDuration(job.start_date, job.end_date)})`;
                            } else if (job.start_date && !job.end_date) {
                                duration = `(${calculateDuration(job.start_date, null)})`;
                                endDate = 'Until today';
                            }
                            let locationDetails = '';
                            if (job.city) locationDetails += job.city;
                            if (job.country) {
                                if (locationDetails) locationDetails += ', ';
                                locationDetails += job.country;
                            }
                            return (
                                <TimelinesWrapper key={`job-${i}`} icon="briefcase" line>
                                    <div className="flex items-center justify-between">
                                        <h4 className="text-md font-semibold">{job.title}</h4>
                                        <span className="text-sm text-gray-700">{job.company}</span>
                                    </div>
                                    <div className="flex items-center gap-2 mt-2">
                                        <span className="text-sm text-gray-700">
                                            {startDate} - {endDate}
                                        </span>
                                        <span className="text-sm text-gray-500">{duration}</span>
                                        <div className="text-sm text-gray-500 ml-auto flex items-center gap-2">
                                            {locationDetails && <span>{locationDetails}</span>}
                                        </div>
                                    </div>
                                    <div className="text-sm text-gray-600 ml-auto flex items-center gap-2 mt-2">
                                        {job.location_type && (
                                            <span className="flex items-center gap-1">
                                                <KeenIcon icon="geolocation" className="text-primary text-sm" />
                                                {job.location_type}
                                            </span>
                                        )}
                                        {job.job_type && (
                                            <span className="flex items-center gap-1">
                                                <KeenIcon icon="compass" className="text-primary text-sm" />
                                                {job.job_type}
                                            </span>
                                        )}
                                    </div>
                                    <div className="card shadow-none p-4 mt-3">
                                        <div className="flex flex-wrap gap-2.5">
                                            <div className="flex flex-col gap-5 grow">
                                                <div className="flex flex-col gap-2">
                                                    <ul className="text-sm text-gray-700">
                                                        {job.responsibilities.map((resp, idx) => (
                                                            <div key={idx} className="flex items-start gap-2.5 w-full">
                                                                <span>
                                                                    <KeenIcon icon="like" className="text-primary text-sm" />
                                                                </span>
                                                                <span className="flex-1 text-sm">{resp}</span>
                                                            </div>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </TimelinesWrapper>
                            );
                        });
                    })()
                ) : (
                    // EDIT MODE: Render editing entries for jobs and gaps.
                    <>
                        <div className="flex space-x-2 mb-4">
                            <button type="button" className="btn btn-success btn-sm" onClick={() => handleAddJob(true)}>
                                <KeenIcon icon="add-notepad" />
                                Add Job
                            </button>
                            <button type="button" className="btn btn-success btn-sm" onClick={() => handleAddGap(true)}>
                                <KeenIcon icon="add-notepad" />
                                Add Gap
                            </button>
                        </div>
                        {editedItems.map((item, index) => (
                            <TimelinesWrapper key={index} icon={item.is_gap ? "abstract-22" : "briefcase"} line>
                                {item.is_gap ? (
                                    <>
                                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 mt-2">
                                            <input
                                                type="date"
                                                className="input input-bordered input-sm w-full sm:w-auto"
                                                value={item.start_date ? item.start_date.slice(0, 10) : ''}
                                                onChange={handleEditField(index, 'start_date')}
                                            />
                                            <span className="text-sm">-</span>
                                            <input
                                                type="date"
                                                className="input input-bordered input-sm w-full sm:w-auto"
                                                value={item.end_date ? item.end_date.slice(0, 10) : ''}
                                                onChange={handleEditField(index, 'end_date')}
                                            />
                                        </div>
                                        <input
                                            type="text"
                                            className="input input-bordered input-sm w-full mt-2"
                                            placeholder="Gap Reason"
                                            value={item.gap_reason || ''}
                                            onChange={handleEditField(index, 'gap_reason')}
                                        />
                                    </>
                                ) : (
                                    <>
                                        <input
                                            type="text"
                                            className="input input-bordered input-sm w-full mt-1"
                                            placeholder="Title"
                                            value={item.title || ''}
                                            onChange={handleEditField(index, 'title')}
                                        />
                                        <input
                                            type="text"
                                            className="input input-bordered input-sm w-full mt-2"
                                            placeholder="Company"
                                            value={item.company || ''}
                                            onChange={handleEditField(index, 'company')}
                                        />
                                        <input
                                            type="text"
                                            className="input input-bordered input-sm w-full mt-2"
                                            placeholder="Location"
                                            value={item.location || ''}
                                            onChange={handleEditField(index, 'location')}
                                        />
                                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 mt-2">
                                            <input
                                                type="date"
                                                className="input input-bordered input-sm w-full sm:w-auto"
                                                value={item.start_date ? item.start_date.slice(0, 10) : ''}
                                                onChange={handleEditField(index, 'start_date')}
                                            />
                                            <span className="text-sm">-</span>
                                            <input
                                                type="date"
                                                className="input input-bordered input-sm w-full sm:w-auto"
                                                value={item.end_date ? item.end_date.slice(0, 10) : ''}
                                                onChange={handleEditField(index, 'end_date')}
                                            />
                                        </div>
                                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 mt-2">
                                            <select
                                                className="select select-bordered select-sm w-full sm:w-auto"
                                                value={item.location_type || ''}
                                                onChange={handleEditField(index, 'location_type')}
                                            >
                                                <option value="">Choose location type</option>
                                                <option value="Onsite">Onsite</option>
                                                <option value="Remote">Remote</option>
                                                <option value="Hybrid">Hybrid</option>
                                            </select>
                                            <select
                                                className="select select-bordered select-sm w-full sm:w-auto"
                                                value={item.job_type || ''}
                                                onChange={handleEditField(index, 'job_type')}
                                            >
                                                <option value="">Choose job type</option>
                                                <option value="Full-time">Full-time</option>
                                                <option value="Part-time">Part-time</option>
                                                <option value="Contract">Contract</option>
                                            </select>
                                        </div>
                                        <div className="card shadow-none p-4 mt-3">
                                            {item.responsibilities.map((resp, respIndex) => (
                                                <div key={respIndex} className="flex items-center gap-2.5 w-full mb-2">
                                                    <input
                                                        type="text"
                                                        className="input input-bordered input-sm flex-1"
                                                        placeholder="Responsibility"
                                                        value={resp}
                                                        onChange={handleEditResponsibility(index, respIndex)}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-ghost btn-sm text-red-500"
                                                        onClick={() => handleDeleteResponsibility(index, respIndex)}
                                                        aria-label={`Delete responsibility ${resp}`}
                                                    >
                                                        &times;
                                                    </button>
                                                </div>
                                            ))}
                                            <div className="flex justify-between">
                                                <button
                                                    type="button"
                                                    className="btn btn-success flex btn-sm"
                                                    onClick={() => handleAddResponsibility(index)}
                                                >
                                                    <KeenIcon icon="add-notepad" />
                                                    Add Responsibility
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}
                                <div className="mt-2 flex justify-end">
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-sm text-red-500"
                                        onClick={() => handleDeleteJob(index)}
                                        aria-label={item.is_gap ? "Delete Gap" : "Delete Job"}
                                    >
                                        <KeenIcon icon="trash" />
                                        {item.is_gap ? "Delete this gap" : "Delete this Job"}
                                    </button>
                                </div>
                            </TimelinesWrapper>
                        ))}
                        <div className="flex space-x-2 mt-4">
                            <button type="button" className="btn btn-success btn-sm" onClick={() => handleAddJob()}>
                                <KeenIcon icon="add-notepad" />
                                Add Job
                            </button>
                            <button type="button" className="btn btn-success btn-sm" onClick={() => handleAddGap()}>
                                <KeenIcon icon="add-notepad" />
                                Add Gap
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export { JobHistory };
